import type { PersonDto } from '@betreuung/shared';
import { Check } from 'lucide-react';
import { RadioCard, RadioGroup } from '@/components/ui/radio-group';
import { Swatch } from '@/components/shared/Swatch';
import { cn } from '@/lib/utils';
import { personVars } from '@/lib/personColor';
import { de } from '@/i18n/de';

export interface PersonRadioCardsProps {
  persons: PersonDto[];
  /** `null` selects "Nicht zugewiesen". */
  value: number | null;
  onChange: (personId: number | null) => void;
}

const UNASSIGNED = 'unassigned';

/**
 * Person choice as a grid of selectable cards.
 *
 * Radio semantics under a card appearance: one tap assigns instead of the two a
 * dropdown would need, and every option shows its colour, so the calendar and
 * this list read the same way.
 */
export function PersonRadioCards({ persons, value, onChange }: PersonRadioCardsProps) {
  return (
    <RadioGroup
      value={value === null ? UNASSIGNED : String(value)}
      onValueChange={(next) => onChange(next === UNASSIGNED ? null : Number(next))}
      aria-label={de.modal.assignTo}
      className="grid grid-cols-2 gap-2"
    >
      {persons.map((person) => {
        const selected = person.id === value;
        return (
          <RadioCard
            key={person.id}
            value={String(person.id)}
            style={personVars(person.colorIndex)}
            className={cn(
              'flex min-h-11 items-center gap-2.5 rounded-control',
              // The selected card carries a 2px border, so its padding drops by
              // one pixel to keep the card's outer size stable.
              selected
                ? 'person-tint-soft border-2 border-(--person) p-[11px]'
                : 'border border-line bg-card p-3',
            )}
          >
            <Swatch colorIndex={person.colorIndex} size={14} radius={4} />
            <span className="min-w-0 flex-1 truncate text-row font-semibold text-fg">
              {person.name}
            </span>
            {selected ? <Check className="size-3.5 shrink-0 text-(--person)" strokeWidth={3} /> : null}
          </RadioCard>
        );
      })}

      {/* Always available and always last, spanning the full width. */}
      <RadioCard
        value={UNASSIGNED}
        className={cn(
          'col-span-2 flex min-h-11 items-center gap-2.5 rounded-control border border-dashed',
          value === null ? 'border-2 border-accent bg-muted p-[11px]' : 'border-free-dash p-3',
        )}
      >
        <Swatch colorIndex={null} size={14} radius={4} />
        <span className="min-w-0 flex-1 truncate text-row font-medium text-fg-muted">
          {de.modal.unassigned}
        </span>
        {value === null ? <Check className="size-3.5 shrink-0 text-fg" strokeWidth={3} /> : null}
      </RadioCard>
    </RadioGroup>
  );
}
